import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, download, errMsg, money, sydDate } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { Field, Notice, PageShell } from "@/components/site/Layout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AddressFields } from "@/pages/store/Checkout";

const label = (s) => (s || "").replace(/_/g, " ");

function Profile({ user, refreshUser }) {
  const [f, setF] = useState({ name: user.name || "", phone: user.phone || "", marketing_opt_in: !!user.marketing_opt_in });
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const act = (fn, ok) => async (e) => { e.preventDefault(); try { const r = await fn(); toast.success(r?.data?.message || ok); refreshUser(); } catch (err) { toast.error(errMsg(err)); } };
  return (
    <div className="grid gap-10 max-w-xl">
      <form className="grid gap-4" onSubmit={act(() => api.put("/account/profile", f), "Profile saved")}>
        <Field label="Name" id="p-name"><input id="p-name" className="hn-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="profile-name-input" /></Field>
        <Field label="Phone" id="p-phone"><input id="p-phone" className="hn-input" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="profile-phone-input" /></Field>
        <label className="flex gap-3 text-sm"><input type="checkbox" className="accent-[#173B2D]" checked={f.marketing_opt_in} onChange={(e) => setF({ ...f, marketing_opt_in: e.target.checked })} data-testid="profile-marketing-checkbox" />Receive HONEY NUTS marketing emails. Order emails are always sent.</label>
        <button className="hn-btn justify-self-start" data-testid="profile-save-btn">Save profile</button>
      </form>
      <form className="grid gap-4" onSubmit={act(() => api.post("/account/change-email", { new_email: email }), "Check your new inbox")}>
        <Field label={`Change email (currently ${user.email})`} id="p-email" hint={user.pending_email ? `Awaiting verification: ${user.pending_email}` : "We'll send a link to verify the new address."}>
          <input id="p-email" type="email" className="hn-input" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="change-email-input" />
        </Field>
        <button className="hn-btn hn-btn-outline justify-self-start" data-testid="change-email-btn">Send verification</button>
      </form>
      <form className="grid gap-4" onSubmit={act(() => api.post("/account/change-password", pw), "Password updated")}>
        {user.has_password && <Field label="Current password" id="p-cpw"><input id="p-cpw" type="password" className="hn-input" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} /></Field>}
        <Field label={user.has_password ? "New password" : "Set a password"} id="p-npw"><input id="p-npw" type="password" className="hn-input" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="new-password-input" /></Field>
        <button className="hn-btn hn-btn-outline justify-self-start">Update password</button>
      </form>
      <div className="border-t border-[#E5DEC9] pt-6">
        <h3 className="text-xl mb-2">Delete account</h3>
        {user.deletion_requested_at ? <p className="text-sm">Deletion requested on {sydDate(user.deletion_requested_at)}.</p> : (
          <button className="text-sm underline text-red-700" onClick={() => window.confirm("Request deletion of your account?") && act(() => api.post("/account/delete-request"), "Request received")({ preventDefault() {} })} data-testid="delete-account-btn">Request account deletion</button>
        )}
      </div>
    </div>
  );
}

function Addresses() {
  const [list, setList] = useState([]);
  const [a, setA] = useState(null);
  const load = () => api.get("/account/addresses").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const save = async (e) => {
    e.preventDefault();
    try { a.id ? await api.put(`/account/addresses/${a.id}`, a) : await api.post("/account/addresses", a); setA(null); load(); toast.success("Address saved"); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="grid gap-4 max-w-2xl">
      {list.length === 0 && !a && <p className="text-sm text-[#5b6b62]">No saved addresses yet.</p>}
      {list.map((x) => (
        <div key={x.id} className="hn-card p-4 flex justify-between text-sm" data-testid="saved-address">
          <span>{x.name}, {x.line1} {x.line2}, {x.suburb} {x.state} {x.postcode}</span>
          <span className="flex gap-3"><button className="underline" onClick={() => setA(x)}>Edit</button><button className="underline" onClick={() => api.delete(`/account/addresses/${x.id}`).then(load)}>Delete</button></span>
        </div>
      ))}
      {a ? (
        <form onSubmit={save} className="hn-card p-5 grid gap-4">
          <Field label="Recipient name" id="ad-n"><input id="ad-n" className="hn-input" value={a.name} onChange={(e) => setA({ ...a, name: e.target.value })} data-testid="address-name-input" /></Field>
          <AddressFields a={a} set={setA} />
          <div className="flex gap-3"><button className="hn-btn" data-testid="address-save-btn">Save address</button><button type="button" className="hn-btn hn-btn-outline" onClick={() => setA(null)}>Cancel</button></div>
        </form>
      ) : <button className="hn-btn hn-btn-outline justify-self-start" onClick={() => setA({ name: "", line1: "", line2: "", suburb: "", state: "NSW", postcode: "" })} data-testid="add-address-btn">Add address</button>}
    </div>
  );
}

function Orders() {
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/account/orders").then((r) => setList(r.data)); }, []);
  if (!list) return <p>Loading…</p>;
  if (!list.length) return <p className="text-sm text-[#5b6b62]" data-testid="orders-empty">You haven't placed any orders yet.</p>;
  return (
    <ul className="divide-y divide-[#E5DEC9] border-y border-[#E5DEC9]">
      {list.map((o) => (
        <li key={o.id}><Link to={`/account/orders/${o.id}`} className="py-4 flex flex-wrap justify-between gap-2 hover:text-[#A66B24]" data-testid="account-order-row">
          <span className="font-semibold">{o.number}{o.is_test && <span className="ml-2 text-xs text-red-700">TEST</span>}</span>
          <span className="text-sm">{sydDate(o.created_at, false)}</span>
          <span className="text-sm capitalize">Payment: {label(o.payment_status)} · Delivery: {label(o.fulfilment_status)}</span>
          <span className="tabular-nums">{money(o.total_cents)}</span>
        </Link></li>
      ))}
    </ul>
  );
}

function Credits() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/account/credits").then((r) => setC(r.data)); }, []);
  if (!c) return <p>Loading…</p>;
  return (
    <div className="grid gap-6">
      <div className="hn-card p-6"><p className="text-sm">Promotional credit available</p><p className="serif text-4xl" data-testid="credit-balance">{money(c.available_cents)}</p>
        {c.reserved_cents > 0 && <p className="text-xs">{money(c.reserved_cents)} reserved in an unfinished checkout</p>}
        <p className="text-xs text-[#5b6b62] mt-2">Promotional credit isn't cash. It applies to merchandise only, excludes delivery and can't be withdrawn or transferred. <Link to="/policies/credit-terms" className="underline">Terms</Link></p></div>
      {c.grants.filter((g) => g.expires_at).map((g) => <p key={g.id} className="text-sm">{money(g.amount_cents)} credit expires {sydDate(g.expires_at, false)}</p>)}
      {c.ledger.length === 0 ? <p className="text-sm text-[#5b6b62]">No credit activity.</p> : (
        <table className="w-full text-sm"><thead><tr className="text-left border-b border-[#E5DEC9]"><th className="py-2">Date</th><th>Activity</th><th className="text-right">Amount</th><th className="text-right">Balance</th></tr></thead>
          <tbody>{c.ledger.map((l) => <tr key={l.id} className="border-b border-[#E5DEC9]/60"><td className="py-2">{sydDate(l.at, false)}</td><td className="capitalize">{l.type}: {l.reason}</td><td className="text-right tabular-nums">{money(l.amount_cents)}</td><td className="text-right tabular-nums">{money(l.balance_after_cents)}</td></tr>)}</tbody></table>
      )}
    </div>
  );
}

export default function Account() {
  const { user, refreshUser, logout } = useStore();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  useEffect(() => { if (user === false) nav("/login?next=/account"); }, [user, nav]);
  if (!user) return <PageShell><p>Loading…</p></PageShell>;
  return (
    <PageShell>
      <div className="flex flex-wrap justify-between items-end gap-4 mb-8">
        <div><p className="eyebrow mb-2">Account</p><h1 className="text-4xl sm:text-5xl">Hello{user.name ? `, ${user.name}` : ""}</h1></div>
        <button className="hn-btn hn-btn-outline" onClick={async () => { await logout(); nav("/"); }} data-testid="logout-btn">Sign out</button>
      </div>
      {!user.email_verified && <div className="mb-6"><Notice tone="warn" testid="account-unverified">{sp.get("welcome") ? "Welcome! " : ""}We've sent a verification link to {user.email}. Verify it to place orders. <button className="underline" onClick={() => api.post("/auth/resend-verification").then(() => toast.success("Link sent")).catch((e) => toast.error(errMsg(e)))} data-testid="account-resend-verification">Resend</button></Notice></div>}
      <Tabs defaultValue="orders">
        <TabsList className="mb-6 flex-wrap h-auto">
          {["orders", "profile", "addresses", "credit"].map((t) => <TabsTrigger key={t} value={t} className="capitalize" data-testid={`account-tab-${t}`}>{t}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="orders"><Orders /></TabsContent>
        <TabsContent value="profile"><Profile user={user} refreshUser={refreshUser} /></TabsContent>
        <TabsContent value="addresses"><Addresses /></TabsContent>
        <TabsContent value="credit"><Credits /></TabsContent>
      </Tabs>
    </PageShell>
  );
}

export function OrderDetail() {
  const { id } = useParams();
  const [o, setO] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { api.get(`/account/orders/${id}`).then((r) => setO(r.data)).catch((e) => setErr(errMsg(e))); }, [id]);
  if (err) return <PageShell narrow><Notice tone="error">{err}</Notice></PageShell>;
  if (!o) return <PageShell narrow><p>Loading…</p></PageShell>;
  return (
    <PageShell narrow>
      <Link to="/account" className="text-sm underline">← Account</Link>
      <h1 className="text-4xl mt-4" data-testid="order-number">Order {o.number}</h1>
      {o.is_test && <p className="text-red-700 font-semibold mt-2">TEST — NOT A REAL PURCHASE</p>}
      <div className="flex flex-wrap gap-6 mt-4 text-sm capitalize">
        <span data-testid="order-payment-status">Payment: {label(o.payment_status)}</span><span data-testid="order-fulfilment-status">Delivery: {label(o.fulfilment_status)}</span>
        {o.refund_status !== "none" && <span>Refund: {label(o.refund_status)}</span>}
      </div>
      <table className="w-full text-sm mt-8"><tbody>
        {o.items.map((i) => <tr key={i.item_id} className="border-b border-[#E5DEC9]"><td className="py-2">{i.name} × {i.qty}</td><td className="text-right tabular-nums">{money(i.line_total_cents)}</td></tr>)}
        <tr><td className="py-1">Subtotal</td><td className="text-right">{money(o.subtotal_cents)}</td></tr>
        <tr><td className="py-1">Promotional credit</td><td className="text-right">-{money(o.credit_cents)}</td></tr>
        <tr><td className="py-1">Delivery</td><td className="text-right">{money(o.shipping_cents)}</td></tr>
        <tr className="font-semibold"><td className="py-2">Total (AUD)</td><td className="text-right">{money(o.total_cents)}</td></tr>
      </tbody></table>
      {o.invoice && <button className="hn-btn mt-6" onClick={() => download(`/account/invoices/${o.invoice.id}/pdf`, `${o.invoice.number}.pdf`)} data-testid="download-invoice-btn">Download invoice {o.invoice.number}</button>}
      <h2 className="text-2xl mt-10 mb-3">Tracking</h2>
      {o.shipments.length === 0 ? <p className="text-sm text-[#5b6b62]">Not dispatched yet.</p> : o.shipments.map((s) => (
        <div key={s.id} className="hn-card p-4 text-sm mb-3" data-testid="order-shipment">
          <p>{sydDate(s.dispatched_at)} · {s.carrier} {s.tracking_number}</p>
          <p className="text-[#5b6b62]">{s.items.map((i) => `${i.name} × ${i.qty}`).join(", ")}</p>
          {s.tracking_url?.startsWith("https://") && <a href={s.tracking_url} target="_blank" rel="noreferrer" className="underline">Track delivery</a>}
        </div>
      ))}
      <h2 className="text-2xl mt-10 mb-3">Timeline</h2>
      <ol className="text-sm grid gap-2">{o.timeline.map((t) => <li key={`${t.at}-${t.event}`}><span className="text-[#5b6b62]">{sydDate(t.at)}</span> — {t.event}</li>)}</ol>
    </PageShell>
  );
}
